/*
 * GA4 storefront measurement: Shopify consent is the source of permission.
 * Do not add input values, search queries, user IDs or purchase events here.
 * GA4 Enhanced measurement must remain disabled in the Web stream settings.
 * https://shopify.dev/docs/api/customer-privacy
 * https://developers.google.com/tag-platform/security/guides/consent
 */
(() => {
  'use strict';
  const element = document.getElementById('kinmilu-analytics');
  if (!element || window.__kinmiluAnalyticsStarted) return;

  const measurementId = element.dataset.measurementId || '';
  const publicTypes = new Set(['index', 'page', 'product', 'collection', 'list-collections', 'blog', 'article', 'search', 'cart', '404']);
  const pageType = element.dataset.pageType;
  if (!/^G-[A-Z0-9]+$/.test(measurementId) || !publicTypes.has(pageType)) return;
  if (location.hostname !== element.dataset.storeHost || location.protocol !== 'https:') return;
  if (window.Shopify?.designMode || new URLSearchParams(location.search).has('preview_theme_id')) return;

  window.__kinmiluAnalyticsStarted = true;
  const disableKey = `ga-disable-${measurementId}`;
  window[disableKey] = true;
  window.dataLayer = window.dataLayer || [];
  const gtag = function () { window.dataLayer.push(arguments); };
  const denied = { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };
  gtag('consent', 'default', denied);
  gtag('set', { ads_data_redaction: true, url_passthrough: false });

  // Drop query strings and fragments before data is passed to Google.
  const cleanUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value);
      return /https?:/.test(url.protocol) ? `${url.origin}${url.pathname}` : '';
    } catch (_) { return ''; }
  };
  const pageData = {
    page_location: pageType === '404' ? `${location.origin}/404` : cleanUrl(location.href),
    page_referrer: cleanUrl(document.referrer),
    page_title: pageType === 'search' ? 'Search | Kinmilu' : pageType === '404' ? 'Page not found | Kinmilu' : element.dataset.pageTitle || 'Kinmilu'
  };
  let loading = false;
  let ready = false;
  let configured = false;
  let sentPageView = false;
  let lastPermission;
  let loadingPrivacy = false;

  const analyticsAllowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };

  const synchronize = () => {
    const allowed = analyticsAllowed();
    // This flag also stops automatic library events immediately on withdrawal.
    window[disableKey] = !allowed;
    if (!ready) {
      if (allowed && !loading) loadGoogle();
      return;
    }
    if (lastPermission !== allowed) {
      gtag('consent', 'update', { ...denied, analytics_storage: allowed ? 'granted' : 'denied' });
      lastPermission = allowed;
    }
    if (!allowed) return;
    if (!configured) {
      gtag('js', new Date());
      gtag('set', pageData);
      gtag('config', measurementId, {
        ...pageData,
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false
      });
      configured = true;
    }
    if (!sentPageView) {
      gtag('event', 'page_view', { ...pageData, send_to: measurementId });
      sentPageView = true;
    }
  };

  function loadGoogle() {
    loading = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onload = () => { ready = true; synchronize(); };
    script.onerror = () => { loading = false; script.remove(); };
    document.head.appendChild(script);
  }

  const initializePrivacy = () => {
    if (window.Shopify?.customerPrivacy) { synchronize(); return; }
    if (loadingPrivacy || !window.Shopify?.loadFeatures) return;
    loadingPrivacy = true;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      loadingPrivacy = false;
      if (!error) synchronize();
    });
  };
  document.addEventListener('visitorConsentCollected', synchronize);
  window.addEventListener('pageshow', initializePrivacy);
  window.addEventListener('load', initializePrivacy, { once: true });
  initializePrivacy();
})();
